import { useEffect, useRef, useState } from 'react';
import { Monitor } from 'lucide-react';

/**
 * 프린터 카메라 스냅샷 폴링.
 *
 * MJPEG long-lived 스트림을 쓰면 브라우저의 origin당 커넥션 6개 제한에 걸려
 * 7번째 이후 프린터 화면이 영영 안 뜬다. 그래서 단발 요청을 반복한다.
 *
 * 22대를 한 화면에 깔기 때문에 주기가 곧 부하다. 목록에서는 느리게,
 * 상세에서는 빠르게 쓰도록 pollInterval을 호출부가 정한다.
 */
// 이 횟수까지 연속 실패하면 잠시 쉰다. 게이트웨이가 낡은 프레임이라도
// 돌려주도록 바뀐 뒤로 진짜 실패는 드물어, 예전처럼 빨리 포기할 이유가 없다.
const FAIL_LIMIT = 20;

export default function CameraFeed({
  printerId,
  cameraStyle,
  className = '',
  pollInterval = 1000,
  pollDelay = 0,
  paused = false,
}) {
  const token = localStorage.getItem('mfg_token');
  // 화면에 걸려 있는 주소. 새로 받아온 사진이 성공했을 때만 이 값을 바꾼다.
  const [shownSrc, setShownSrc] = useState(null);
  // 첫 장을 받았는지. 폴링 주기를 가르는 기준이라 따로 둔다(사진마다 바뀌면 안 됨).
  const [hasFrame, setHasFrame] = useState(false);
  const [errCount, setErrCount] = useState(0);
  const hasNoCamera = cameraStyle === 'none';
  const pendingRef = useRef(null);

  // <img src> 를 직접 갈아끼우지 않는다.
  //
  // 왜: src 를 바꿨는데 그 요청이 실패하면 브라우저가 멀쩡히 떠 있던 사진을
  // 지우고 깨진 이미지 아이콘을 보여준다. 대기 중 프린터는 20초마다 한 번
  // 갱신하므로, 한 번만 실패해도 그 칸이 20초 동안 빈 칸이 된다
  // (실측: 화면을 3분 열어 두면 표시되는 카메라가 16대에서 9대로 줄었다).
  // 그래서 보이지 않는 Image 로 먼저 받아 보고, 성공한 것만 화면에 올린다.
  // 실패하면 직전 사진이 그대로 남는다 — 관제에는 몇 초 지난 사진이 빈 칸보다 낫다.
  useEffect(() => {
    if (hasNoCamera || paused) return undefined;

    let stopped = false;
    const fetchOne = () => {
      const url = `/api/printers/${printerId}/camera/snapshot?token=${token}&t=${Date.now()}`;
      const img = new Image();
      pendingRef.current = img;
      img.onload = () => {
        if (stopped) return;
        setShownSrc(url);
        setHasFrame(true);
        setErrCount(c => (c > 0 ? 0 : c));
      };
      img.onerror = () => {
        if (stopped) return;
        setErrCount(c => c + 1);
      };
      img.src = url;
    };

    // 아직 한 장도 못 받았으면 지정 주기를 기다리지 않고 자주 다시 묻는다.
    // 대기 중 프린터의 주기는 20초라, 첫 시도가 어긋나면 화면을 연 사람이
    // 20초를 빈 칸만 보고 있어야 한다. 첫 장을 받은 뒤부터 제 주기로 돌아간다.
    const effectiveInterval = hasFrame ? pollInterval : Math.min(pollInterval, 3000);

    let iv;
    // 시작 시점을 흩어 동시 요청이 몰리지 않게 한다
    const to = setTimeout(() => {
      fetchOne();
      iv = setInterval(fetchOne, effectiveInterval);
    }, pollDelay);

    return () => {
      stopped = true;
      clearTimeout(to);
      clearInterval(iv);
      if (pendingRef.current) {
        pendingRef.current.onload = null;
        pendingRef.current.onerror = null;
      }
    };
  }, [hasNoCamera, paused, pollInterval, pollDelay, printerId, token, hasFrame]);

  // 연속 실패가 쌓이면 잠시 쉬었다 다시 시도한다 (일시적 오류 대응)
  useEffect(() => {
    if (errCount < FAIL_LIMIT) return undefined;
    const t = setTimeout(() => setErrCount(0), 15000);
    return () => clearTimeout(t);
  }, [errCount]);

  // 한 번이라도 떴던 카메라는 실패해도 회색 상자로 바꾸지 않는다.
  // 아직 한 번도 못 뜬 카메라만 안내를 띄운다.
  if (hasNoCamera || (!shownSrc && errCount >= FAIL_LIMIT)) {
    return (
      <div className={`bg-gray-800 flex items-center justify-center ${className}`}>
        <div className="text-center">
          <Monitor size={20} className="text-gray-600 mx-auto mb-1" />
          <p className="text-gray-600 text-[10px]">{hasNoCamera ? '카메라 없음' : '재연결 중...'}</p>
        </div>
      </div>
    );
  }

  // 첫 사진을 받기 전에는 빈 칸을 둔다. 깨진 이미지 아이콘이 뜨지 않도록
  // src 가 있을 때만 <img> 를 건다.
  if (!shownSrc) {
    return <div className={`bg-gray-800 ${className}`} />;
  }

  return (
    <img
      src={shownSrc}
      alt=""
      className={`object-cover bg-gray-800 ${className}`}
    />
  );
}
